/**
 * Cart drawer upsell carousel.
 *
 * Curated mode: the cards are server-rendered by
 * snippets/cart-drawer-upsell.liquid and stay fresh through cart re-renders.
 *
 * Automatic mode: this component fetches complementary recommendations for a
 * product in the cart from sections/cart-drawer-upsell.liquid and injects the
 * cards. Because every cart change replaces the drawer's innerHTML, injected
 * cards do not survive it — so the fetched markup is cached at MODULE scope
 * and re-injected when the element reconnects, which costs no extra request.
 *
 * Adds are stamped with `_upsell` plus the standard attribution line
 * properties. The theme never writes `_iom_*`: the IOM engine stamps its own
 * offer context on the request (see .cursor/references/cart-architecture.md).
 */
(() => {
  /** Recommendation cards markup per seed product id. */
  const recommendationsCache = new Map();

  /**
   * "Buy it again" card markup per product id, harvested from the
   * #cart-drawer-upsell-cart-products block.
   *
   * The recommendations API never returns the seed product itself, so these
   * are the only way a product already in the cart can appear. The section
   * only renders cards for buy-again ELIGIBLE products, so an empty string is
   * cached for one it checked and rejected — that is the "already checked"
   * signal that stops us refetching for it on every cart update.
   */
  const buyAgainCardCache = new Map();

  const ADDED_STATE_DURATION = 2000;

  /**
   * The message for an add that got no answer, carried across the re-render
   * that follows it. Module scope because that render replaces this element
   * and its cards (the carousel sits inside #MiniCart-<section>). Keyed by
   * PRODUCT: the re-rendered card comes back on its default variant, so the
   * attempted variant cannot be used to find it. `source` is the element that
   * failed, which must not consume the message before the re-render.
   * @type {{productId: string, variantId: string, message: string, at: number, source: HTMLElement} | null}
   */
  let pendingError = null;

  /** A carried message older than this is dropped rather than shown late. */
  const PENDING_ERROR_TTL = 30000;

  if (customElements.get('cart-upsell')) return;

  customElements.define(
    'cart-upsell',
    class CartUpsell extends BaseElement {
      get isAutomatic() {
        return this.hasAttribute('data-automatic');
      }

      /** When set, in-cart products stay on offer and can be added again. */
      get showInCart() {
        return this.hasAttribute('data-show-in-cart');
      }

      get track() {
        return this.querySelector('[data-upsell-track]');
      }

      get controls() {
        return this.querySelector('[data-upsell-controls]');
      }

      get prevButton() {
        return this.querySelector('[data-upsell-prev]');
      }

      get nextButton() {
        return this.querySelector('[data-upsell-next]');
      }

      get inCartProductIds() {
        return new Set(
          (this.getAttribute('data-cart-product-ids') || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );
      }

      /**
       * Products whose cart line carries `_upsell` — already taken from this
       * carousel. Filtered out in either mode, and back on offer as soon as
       * the line is removed.
       */
      get consumedProductIds() {
        return new Set(
          (this.getAttribute('data-upsell-product-ids') || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );
      }

      connectedCallback() {
        super.connectedCallback();

        this.syncArrowStateBound = this.syncArrowState.bind(this);
        this.loadedSeedId = null;
        this.loadToken = 0;

        if (this.track) this.on(this.track, 'scroll', this.syncArrowStateBound, { passive: true });
        if (this.prevButton) this.on(this.prevButton, 'click', this.handlePrev.bind(this));
        if (this.nextButton) this.on(this.nextButton, 'click', this.handleNext.bind(this));

        // Delegated: cards arrive after connect in automatic mode.
        this.on(this, 'change', (event) => {
          if (event.target.matches('.cart-upsell__variant-select')) this.handleVariantChange(event);
        });
        this.on(this, 'submit', (event) => {
          if (event.target.matches('.cart-upsell__form')) void this.handleAddToCart(event);
        });

        const unsubscribe = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.handleCartUpdate.bind(this)
        );
        this.registerCleanup(unsubscribe);

        this.drawer = this.closest('cart-drawer');
        if (this.drawer) {
          // A closed drawer is display:none, so the track has no layout and
          // the arrows cannot be sized until it opens.
          this.on(this.drawer, 'drawer:afterShow', () => {
            if (this.isAutomatic && this.loadedSeedId === null) void this.loadRecommendations();
            requestAnimationFrame(this.syncArrowStateBound);
          });
        }

        this.applyCaps();
        this.syncArrowState();
        // Curated cards are server-rendered and final now (consumed products
        // are left out, not hidden). Automatic cards are not injected yet:
        // loadRecommendations settles them.
        this.cardsSettled = !this.isAutomatic;
        this.showPendingError();

        if (this.isAutomatic) {
          // Defer the fetch until the drawer is actually open: the carousel
          // lives inside a closed drawer, so fetching on connect would spend a
          // Section Rendering request per page view on content the shopper may
          // never see. Cached seeds re-inject immediately, and the drawer opens
          // itself on add.
          const alreadyOpen = !this.drawer || this.drawer.open;
          if (alreadyOpen || recommendationsCache.has(this.getAttribute('data-first-product-id'))) {
            void this.loadRecommendations();
          }
        }
      }

      /** Disables add buttons for variants already at the per-variant cap. */
      applyCaps(cart) {
        if (!theme.cartLimits || theme.cartLimits.max <= 0) return;
        if (cart) {
          theme.cartLimits.applyVariantCaps(this, cart);
          return;
        }
        theme.cartState
          .fetch()
          .then((fetched) => {
            if (this.isConnected) theme.cartLimits.applyVariantCaps(this, fetched);
          })
          .catch(() => {
            // A failed cart read leaves the buttons as rendered.
          });
      }

      /**
       * Cart changed. A re-render normally replaces this element with fresh
       * server-rendered attributes, so this only has to cover payloads that
       * carry no section HTML (error paths, out-of-band updates).
       */
      handleCartUpdate(event) {
        const cart = event?.cart;
        if (!cart?.items) return;

        this.applyCaps(cart);

        this.setAttribute('data-cart-product-ids', cart.items.map((item) => String(item.product_id)).join(','));
        this.setAttribute(
          'data-upsell-product-ids',
          cart.items
            .filter((item) => item.properties && item.properties._upsell)
            .map((item) => String(item.product_id))
            .join(',')
        );

        if (!this.isAutomatic) return;
        if (this.showInCart) void this.syncBuyAgainCards();
        else {
          this.filterCards();
          this.showPendingError();
        }
      }

      async loadRecommendations() {
        const firstProductId = this.getAttribute('data-first-product-id');
        if (!firstProductId || !this.getAttribute('data-recommendations-url')) {
          this.hidden = true;
          this.settleCards();
          return;
        }

        this.loadedSeedId = firstProductId;
        const token = ++this.loadToken;

        // Complementary recommendations only exist for products configured in
        // Search & Discovery, so when the first cart item has none, fall back
        // through the other products in the cart rather than hiding the
        // carousel for the rest of the session.
        const seedIds = [firstProductId, ...this.inCartProductIds].filter(
          (id, index, ids) => ids.indexOf(id) === index
        );

        try {
          let recommendationsHTML = '';
          const freshlyFetched = new Set();

          if (recommendationsCache.get(firstProductId) === undefined) {
            freshlyFetched.add(firstProductId);
            await this.fetchSectionDoc(firstProductId);
            if (token !== this.loadToken) return;
          }

          for (const seedId of seedIds) {
            let cardsHTML = recommendationsCache.get(seedId);

            if (cardsHTML === undefined) {
              freshlyFetched.add(seedId);
              await this.fetchSectionDoc(seedId);
              cardsHTML = recommendationsCache.get(seedId) ?? '';
            }

            if (token !== this.loadToken) return;
            if (!cardsHTML) continue;

            if (this.showInCart) {
              // Buy-again cards are appended below; the first seed with
              // recommendations wins.
              recommendationsHTML = cardsHTML;
              break;
            }

            this.track.innerHTML = cardsHTML;
            if (this.filterCards() > 0) {
              this.applyCaps();
              this.settleCards();
              return;
            }
          }

          if (!this.showInCart) {
            // Every seed came up empty, or everything is already in the cart.
            this.track.innerHTML = '';
            this.filterCards();
            this.settleCards();
            return;
          }

          // Skip the refetch when the primary seed was fetched during THIS
          // load: its checked-ids sentinel already recorded every product the
          // section evaluated, so an identical refetch returns identical data.
          const missingBuyAgain = [...this.inCartProductIds].some((id) => !buyAgainCardCache.has(id));
          if (missingBuyAgain && !freshlyFetched.has(firstProductId)) {
            await this.fetchSectionDoc(firstProductId);
            if (token !== this.loadToken) return;
          }

          this.track.innerHTML = recommendationsHTML;
          this.appendBuyAgainCards();
          this.filterCards();
          this.applyCaps();
          this.settleCards();
        }
        catch (error) {
          console.error(error);
          this.hidden = true;
          this.settleCards();
        }
      }

      /**
       * Marks the track as final for this render and settles any carried
       * message against it. Only the end of a load counts: automatic mode
       * injects each fallback seed and filters it on the way, and settling on
       * one of those passes dropped the message of an add made from a later
       * seed's card before that card was painted.
       */
      settleCards() {
        this.cardsSettled = true;
        this.showPendingError();
      }

      /**
       * Fetches the recommendations section for one seed and harvests both
       * card sources from it.
       * @param {string} seedId
       */
      async fetchSectionDoc(seedId) {
        const url = new URL(this.getAttribute('data-recommendations-url'), window.location.origin);
        url.searchParams.set('section_id', this.getAttribute('data-section-id') || 'cart-drawer-upsell');
        url.searchParams.set('product_id', seedId);
        url.searchParams.set('limit', this.getAttribute('data-limit') || '3');
        url.searchParams.set('intent', 'complementary');

        const response = await fetch(url.toString());
        // Without this check a 429 or bot-challenge HTML body parses fine,
        // yields no products block, and caches '' — permanently marking that
        // seed "no recommendations" for the page's lifetime. Throwing leaves
        // the cache untouched so a later open retries.
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        recommendationsCache.set(
          seedId,
          doc.getElementById('cart-drawer-upsell-products')?.innerHTML.trim() ?? ''
        );

        const cartProductsBlock = doc.getElementById('cart-drawer-upsell-cart-products');

        for (const card of cartProductsBlock?.querySelectorAll('.cart-upsell__card') ?? []) {
          const productId = card.getAttribute('data-product-id');
          if (!productId) continue;
          card.setAttribute('data-buy-again', '');
          buyAgainCardCache.set(productId, card.outerHTML);
        }

        // Products the section checked but rendered no card for are not
        // eligible: cache the empty sentinel, never overwriting a real card.
        for (const id of (cartProductsBlock?.getAttribute('data-checked-product-ids') ?? '').split(',')) {
          const productId = id.trim();
          if (productId && !buyAgainCardCache.has(productId)) buyAgainCardCache.set(productId, '');
        }
      }

      /**
       * Prepends a buy-again card for every product seen in the cart this
       * session that has no card in the track yet — they lead the carousel.
       * Cards persist after their product leaves the cart, so a removed item
       * comes back as a re-add offer.
       */
      appendBuyAgainCards() {
        const { track } = this;
        if (!track) return;

        const consumed = this.consumedProductIds;
        const present = new Set(
          [...track.querySelectorAll('.cart-upsell__card')].map((card) => card.getAttribute('data-product-id'))
        );

        // Current cart products lead (newest first), then everything else seen
        // in the cart this session.
        const offerIds = new Set([...this.inCartProductIds, ...buyAgainCardCache.keys()]);

        const missing = [];
        for (const id of offerIds) {
          if (present.has(id) || consumed.has(id)) continue;
          const cardHTML = buyAgainCardCache.get(id);
          if (cardHTML) missing.push(cardHTML);
        }

        if (missing.length > 0) track.insertAdjacentHTML('afterbegin', missing.join(''));
      }

      /** Reconciles buy-again cards after a cart change. */
      async syncBuyAgainCards() {
        const token = this.loadToken;
        const { track } = this;
        if (!track) return;

        const consumed = this.consumedProductIds;
        const needsFetch = [...this.inCartProductIds].some(
          (id) =>
            !consumed.has(id) &&
            !buyAgainCardCache.has(id) &&
            !track.querySelector(`.cart-upsell__card[data-product-id="${id}"]`)
        );

        if (needsFetch) {
          const seedId = this.loadedSeedId ?? [...this.inCartProductIds][0];
          if (seedId) {
            try {
              await this.fetchSectionDoc(seedId);
            }
            catch (error) {
              console.error(error);
            }
          }
          // A reseed started while we were fetching; it renders the track.
          if (token !== this.loadToken) return;
        }

        this.appendBuyAgainCards();
        this.filterCards();
        this.applyCaps();
        this.showPendingError();
      }

      /**
       * Hides cards for products already in the cart, unless show-in-cart
       * keeps them addable. A product added FROM the carousel is hidden either
       * way — that offer has been taken.
       * @returns {number} Visible card count.
       */
      filterCards() {
        const { track } = this;
        if (!track) return 0;

        const inCart = this.inCartProductIds;
        const consumed = this.consumedProductIds;
        const showInCart = this.showInCart;
        let visible = 0;

        for (const card of track.querySelectorAll('.cart-upsell__card')) {
          const productId = card.getAttribute('data-product-id');
          const hide = !!productId && inCart.has(productId) && (!showInCart || consumed.has(productId));
          card.hidden = hide;
          if (!hide) visible += 1;
        }

        this.hidden = visible === 0;
        if (this.controls) this.controls.hidden = visible <= 1;
        this.syncArrowState();

        return visible;
      }

      syncArrowState() {
        const { track, prevButton, nextButton } = this;
        if (!track || !prevButton || !nextButton) return;

        // A closed drawer is display:none, so every scroll metric is 0 and
        // both arrows would be marked disabled — leaving desktop shoppers
        // unable to reach later cards after an add. Leave the last known
        // state; drawer:afterShow re-syncs once there is a layout.
        if (track.clientWidth === 0) return;

        prevButton.setAttribute('aria-disabled', String(track.scrollLeft <= 2));
        nextButton.setAttribute(
          'aria-disabled',
          String(track.scrollLeft + track.clientWidth >= track.scrollWidth - 2)
        );
      }

      handlePrev() {
        if (this.prevButton?.getAttribute('aria-disabled') === 'true') return;
        this.scrollByCard(-1);
      }

      handleNext() {
        if (this.nextButton?.getAttribute('aria-disabled') === 'true') return;
        this.scrollByCard(1);
      }

      scrollByCard(direction) {
        const { track } = this;
        if (!track) return;
        const card = track.querySelector('.cart-upsell__card:not([hidden])');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const distance = card ? card.offsetWidth + gap : track.clientWidth;
        track.scrollBy({ left: distance * direction, behavior: 'smooth' });
      }

      /**
       * Syncs a card with the variant chosen in its select. The select carries
       * no name, so the hidden input[name='id'] stays the single field IOM
       * resolves and the form posts — everything the variant drives is updated
       * here.
       */
      handleVariantChange(event) {
        const select = event.target;
        const card = select.closest('.cart-upsell__card');
        const option = select.selectedOptions[0];
        if (!card || !option) return;

        const form = card.querySelector('.cart-upsell__form');
        const idInput = form?.querySelector('input[name="id"]');
        if (idInput) idInput.value = option.value;

        const button = form?.querySelector('button[type="submit"]');
        if (button) {
          const available = option.getAttribute('data-available') !== 'false';
          const label = button.querySelector('.cart-upsell__add-label');
          const icon = button.querySelector('.cart-upsell__add-icon');

          button.setAttribute('data-variant-id', option.value);
          button.setAttribute('data-available', String(available));
          button.disabled = !available;
          if (icon) icon.hidden = !available;
          if (label) {
            label.textContent = available
              ? button.getAttribute('data-add-label')
              : button.getAttribute('data-sold-out-label');
          }
        }

        // One money value per node (IOM contract): swap the text, never append.
        const priceValue = card.querySelector('.cart-upsell__price-value');
        if (priceValue) {
          if (option.getAttribute('data-price')) priceValue.textContent = option.getAttribute('data-price');
          priceValue.setAttribute('data-variant-id', option.value);
        }

        let compare = card.querySelector('.cart-upsell__compare-price');
        const compareAt = option.getAttribute('data-compare-at-price');
        if (compareAt) {
          if (!compare) {
            compare = document.createElement('s');
            compare.className = 'cart-upsell__compare-price';
            card.querySelector('.cart-upsell__price')?.prepend(compare);
          }
          compare.textContent = compareAt;
        }
        else if (compare) {
          compare.remove();
        }

        if (form?.dataset.attributionLineProperties) {
          try {
            const properties = JSON.parse(form.dataset.attributionLineProperties);
            const sku = option.getAttribute('data-sku');
            if (sku) properties._variant_sku = sku;
            else delete properties._variant_sku;
            form.dataset.attributionLineProperties = JSON.stringify(properties);
          }
          catch (error) {
            // Malformed JSON: leave the server-rendered value alone.
          }
        }

        // The button was just re-enabled from availability alone, so re-check
        // the new variant against the per-variant cap.
        this.applyCaps();
      }

      /**
       * Adds the card's variant. Intercepted rather than left to theme.js's
       * ProductForm, which carries product-page assumptions; with no JS the
       * form posts natively and `properties[_upsell]` still applies.
       */
      async handleAddToCart(event) {
        event.preventDefault();
        event.stopPropagation();

        const form = event.target;
        const button = form.querySelector('button[type="submit"]');
        if (!button || button.disabled) return;

        const formData = new FormData(form);
        const variantId = String(formData.get('id') || button.getAttribute('data-variant-id') || '');
        if (!variantId) return;

        // Claimed before the cap check's cart read, not after it: a second
        // click during that read found the button still enabled and posted a
        // second /cart/add (the engine's lock orders two adds, it cannot merge
        // them). Clearing the last failure here also stops a stale message
        // from sitting beside the new attempt.
        const label = button.querySelector('.cart-upsell__add-label');
        const originalLabel = label?.textContent;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (label && button.getAttribute('data-adding-label')) {
          label.textContent = button.getAttribute('data-adding-label');
        }
        this.clearAddError(form);
        // A new attempt supersedes any failure still being carried, so an
        // unrelated later re-render cannot resurface it.
        pendingError = null;

        // Per-variant cap (settings.cart_max_per_variant), enforced here and
        // not only by the disabled state: the button is re-enabled from
        // availability on every variant change, and the cap is re-checked
        // asynchronously, so a fast click can arrive while it still looks
        // addable. ProductForm makes the same check for the product page.
        const cap = theme.cartLimits ? theme.cartLimits.max : 0;
        if (cap > 0) {
          let cart = null;
          try {
            cart = await theme.cartState.fetch();
          }
          catch (error) {
            // Cart unreadable: fall through rather than block a legitimate add.
          }
          if (cart && theme.cartLimits.variantQuantityInCart(cart, variantId) + 1 > cap) {
            // Hand the button back, then let the cap decide its state.
            if (label) label.textContent = originalLabel;
            button.removeAttribute('aria-busy');
            button.disabled = false;
            theme.cartLimits.applyVariantCaps(this, cart);
            const capMessage = theme.cartLimits.message();
            if (capMessage) {
              button.title = capMessage;
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
                source: 'cart-upsell',
                productVariantId: variantId,
                errors: capMessage,
                message: capMessage,
              });
            }
            return;
          }
        }

        formData.set('quantity', '1');

        // Standard attribution properties (_variant_sku, _product_id, ...)
        // plus the upsell flag. Never any _iom_*: the IOM engine stamps its
        // own offer context on the request.
        const attribution = window.Attribution;
        if (attribution && typeof attribution.buildLineItemProperties === 'function') {
          const properties = attribution.buildLineItemProperties(form, variantId) || {};
          if (!properties._upsell) properties._upsell = 'true';
          for (const [key, value] of Object.entries(properties)) {
            formData.set(`properties[${key}]`, String(value));
          }
        }
        else {
          formData.set('properties[_upsell]', 'true');
        }

        // Every write from the carousel takes the cart engine's turn: a direct
        // POST could overlap an in-flight quantity change, and either
        // response's render could paint over the other. cart.js always loads
        // with this drawer, but the lookup is at click time and degrades to an
        // unlocked POST rather than a dead button.
        const engine = theme.cartEngine;
        const exclusive = engine && typeof engine.runExclusive === 'function'
          ? (task) => engine.runExclusive(task)
          : (task) => task({ fetch: (url, options) => fetch(url, options) });

        let added = false;
        try {
          added = await exclusive(async (io) => {
            // Gathered inside the turn, so it describes the cart at write time.
            const sections = [];
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
            );
            formData.set('sections', sections.join(','));
            formData.set('sections_url', window.location.pathname);

            const config = theme.utils.fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = formData;

            const response = await io.fetch(theme.routes.cart_add_url, config);
            const text = await response.text();
            let parsedState = null;
            try {
              parsedState = JSON.parse(text);
            }
            catch (error) {
              // Rate limiting (429) answers with an HTML body.
            }

            // The server answered, so nothing was added: show why on the card
            // and leave the drawer alone. A refresh would replace the card and
            // wipe the message, and the cart already matches the server.
            if (!response.ok || !parsedState || parsedState.status) {
              const message = parsedState?.description || parsedState?.message || this.addErrorMessage();
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
                source: 'cart-upsell',
                productVariantId: variantId,
                errors: parsedState?.errors || parsedState?.description || message,
                message: parsedState?.message || message,
              });
              console.error('[cart-upsell] add rejected', response.status, message);
              this.showAddError(form, message);
              return false;
            }

            // Shared, deduplicated read: the drawer's own refresh joins it.
            let cart = null;
            try {
              cart = { ...(await theme.cartState.fetch({ fresh: true })) };
            }
            catch (error) {
              console.error(error);
            }

            if (cart) {
              cart.sections = parsedState.sections;
              // cart-items re-renders the drawer from this payload, which also
              // re-renders this carousel with fresh server state.
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-upsell',
                productVariantId: variantId,
                cart,
              });
            }
            else {
              // The add landed but the cart could not be read back: re-render
              // from server truth instead of publishing a cart without items.
              this.requestRefresh();
            }
            // attribution.js listens for this and syncs cart attributes; calling
            // its sync directly here would double the write.
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
            return true;
          });
        }
        catch (error) {
          // No answer (network failure, or the engine's 15s ceiling): the add
          // may or may not have landed, so re-render from server truth. The
          // message rides across that swap at module scope and is settled on
          // the re-rendered cards (showPendingError): shown if the offer is
          // still on screen, dropped if a landed add consumed it.
          console.error(error);
          const message = this.addErrorMessage();
          // Shown on this card now AND carried to the re-render: the refresh
          // can fail too (offline), and then nothing replaces this element.
          this.showAddError(form, message);
          pendingError = {
            productId: form.getAttribute('data-product-id')
              || form.closest('.cart-upsell__card')?.getAttribute('data-product-id')
              || '',
            variantId,
            message,
            at: Date.now(),
            source: this,
          };
          if (label) label.textContent = originalLabel;
          this.requestRefresh();
        }

        if (added && button.isConnected && label && button.getAttribute('data-added-label')) {
          label.textContent = button.getAttribute('data-added-label');
          setTimeout(() => {
            if (!button.isConnected) return;
            if (label.textContent === button.getAttribute('data-sold-out-label')) return;
            label.textContent = originalLabel;
            button.disabled = false;
          }, ADDED_STATE_DURATION);
        }

        if (button.isConnected) {
          button.removeAttribute('aria-busy');
          if (label && (!label.textContent || label.textContent === button.getAttribute('data-adding-label'))) {
            label.textContent = originalLabel;
          }
          if (label && label.textContent === originalLabel) button.disabled = false;
        }
      }

      addErrorMessage() {
        return theme.cartStrings?.error || "Couldn't add this item. Try again.";
      }

      requestRefresh() {
        const engine = theme.cartEngine;
        if (engine && typeof engine.requestRefresh === 'function') engine.requestRefresh();
        else document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
      }

      /** The message slot of the card that owns `form`. */
      errorSlotFor(form) {
        return form?.closest('.cart-upsell__card')?.querySelector('[data-cart-upsell-error]') || null;
      }

      showAddError(form, message) {
        const slot = this.errorSlotFor(form);
        if (!slot || !message) return;
        slot.textContent = message;
        slot.hidden = false;
      }

      clearAddError(form) {
        const slot = this.errorSlotFor(form);
        if (!slot) return;
        slot.textContent = '';
        slot.hidden = true;
      }

      /**
       * Settles a message left by an add that got no answer, once, against the
       * first FINAL set of cards painted after the re-render it triggered
       * (`cardsSettled`; see settleCards). Shown if that product's card is on
       * screen, dropped otherwise.
       *
       * Settled once rather than held until a visible card turned up: holding
       * it kept a landed add's message alive (its card is hidden as consumed,
       * or left out by curated Liquid), and removing that line later showed a
       * false failure. Matched by PRODUCT: the card re-renders on its default
       * variant, so it is switched back to the variant that was tried.
       */
      showPendingError() {
        if (!pendingError || pendingError.source === this || !this.cardsSettled) return;

        const pending = pendingError;
        pendingError = null;
        if (Date.now() - pending.at > PENDING_ERROR_TTL || !pending.productId) return;

        const card = this.track?.querySelector(`.cart-upsell__card[data-product-id="${CSS.escape(pending.productId)}"]`);
        // Gone, hidden as consumed, or the whole carousel is hidden: the add
        // landed or the offer is not on screen, so there is nothing to report.
        if (!card || card.hidden || this.hidden) return;

        // The card re-renders on its default variant. Put it back on the one
        // the shopper tried, through the select so price, image and the
        // button's variant follow (handleVariantChange).
        const select = card.querySelector('.cart-upsell__variant-select');
        if (select && select.value !== pending.variantId
          && Array.from(select.options).some((option) => option.value === pending.variantId)) {
          select.value = pending.variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const form = card.querySelector('.cart-upsell__form');
        if (form) this.showAddError(form, pending.message);
      }
    }
  );
})();
